import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { I18NextPipe } from 'angular-i18next';
import { MbsPopupType, MbsSize } from 'mbs-ui-kit';
import { ModalService, ModalSettings } from 'mbs-ui-kit/modal/modal.service';
import { ToastService } from 'mbs-ui-kit/toast/toast.service';
import { GuidEmpty } from 'mbs-ui-kit/utils/constants';
import { Administrator, RoutingPath, UiStorageKey } from 'mbs-ui-shared';
import { CantOpenModalComponent } from 'mbs-ui-shared@components/cant-open-modal/cant-open-modal.component';
import { ApplicationStateFacade } from 'mbs-ui-shared@facades/application.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LoginFacade } from 'mbs-ui-shared@facades/login.facade';
import { NotificationOptionsFacade } from 'mbs-ui-shared@facades/notificationOptions.facade';
import { RemoteDeployDataFacade } from 'mbs-ui-shared@facades/remote-deploy-data.facade';
import Computer from 'mbs-ui-shared@models/Computer';
import { NotificationPerPlanSettings } from 'mbs-ui-shared@models/Notification';
import { Period } from 'mbs-ui-shared@models/Period.enum';
import { PlanMode, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RecurType } from 'mbs-ui-shared@models/RecurType.enum';
import { StorageAccountLegacy } from 'mbs-ui-shared@models/StorageAccount';
import { MyTreeElements } from 'mbs-ui-shared@models/backup/storages-type';
import { RemoteDeployPlanModel } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-plan-model';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { RmCommandsService } from 'mbs-ui-shared@services/rm-commands.service';
import { updatePlanBucketDisplayName } from 'mbs-ui-shared@utils/bucketDisplayNameToPlan';
import { ScheduleDataHelper } from 'mbs-ui-shared@utils/schedule-data-helper';
import { BehaviorSubject, from, noop, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, first, map, switchMap, take } from 'rxjs/operators';
import { WizardBaseForPlanHelper } from 'wizards/helpers/bases/wizard-base-helpers';
import { StepsDataFromPlanHelper } from 'wizards/helpers/steps-data-from-plan-helper/steps-data-from-plan-helper';
import { DiskInfoCommunication } from 'wizards/models/base/base-models/plan-disk-info-model';
import { CreateRVPlanParams, PlansWizardMode, TargetComputerData } from 'wizards/models/base/base-plan-models';
import { SaveVariants } from 'wizards/models/base/other-base-models/save-variants';
import { RestoreTypes } from 'wizards/models/base/restore-types-model';
import { RestoreIbbTypeModel } from 'wizards/models/restoreIBB/restore-ibb-type-model';
import { FromTime } from 'wizards/models/retention-policy-models';
import { BackupTargetVolumesEnum } from 'wizards/models/what-backup-models';
import { GenericRestoreWizardComponent } from 'wizards/remote-management-wizards/generic-restore-wizard/generic-restore-wizard.component';
import { MachinesDisksLoadedStatuses } from 'wizards/services/wizard-steps.service';

const AGENT_VERSION_FOR_APP_AWARE = 79;
const AGENT_VERSION_FOR_CHANGE_TYPE = 990;
const VERSION_RECOVERY_MODEL = 810297;

export interface OverdueAfterSuccess {
  enabled: boolean;
  amount: number;
  period: number;
}

export type afterCloseType = { afterClosed: Observable<any> };

export interface SQLIsSysAdminOrCredentialsModel {
  hid: string;
  profile: string;
  isWindowsAuth: boolean;
  instanceName: string;
  userName: string;
  password: string;
  planId: string;
}

export type WizardState = {
  isSQLPlan: boolean;
  isRDMode: boolean;
  isRestore: boolean;
  isHyperV: boolean;
  isVMWare: boolean;
  isAppAwareEnabled: boolean;
  isClusterSupported?: boolean;
};

export type WizardRestoreSettings = {
  item: MyTreeElements;
  isLocalStorage: boolean;
  isLocallyLocalStorage: boolean;
  canRestoreToDifferentComputer: boolean;
  isRemote: boolean;
};

export interface WizardSettings {
  fromGettingStarted?: boolean;
  compFullName?: string;
  compName?: string;
  compVmName?: string;
  email?: string;
  hid: string;
  isNBF?: boolean;
  isNetBuild?: boolean;
  isOffline?: boolean;
  mode: PlanMode;
  planId?: string;
  sourceHid?: string;
  sourceUserId?: string;
  profile?: string;
  rdConfigurationId?: string;
  type?: PlanTypes;
  userId?: string;
  initialFormData?: { [key: string]: any };
  wizardMode?: PlansWizardMode;
  backupVersion?: string;
  wasNotExecuted?: boolean;
  isLinux?: boolean;
  isMac?: boolean;
  isUnix?: boolean;
  supressShowingBackupFormatDialog?: boolean;
  sessionId?: string;
  targetComputerData?: TargetComputerData;
  firstStepTitle?: string;
  restoreParams?: WizardRestoreSettings;
}

export abstract class RemoteManagementWizardsServiceAbstract implements WizardSettings {
  public readonly vmTypes: PlanTypes[] = [
    PlanTypes.RestoreVMware,
    PlanTypes.RestoreVMwareToCustom,
    PlanTypes.RestoreHyperV,
    PlanTypes.RestoreHyperVToCustom
  ];

  public loadedDisksInfoForIbb: WritableSignal<{ volumes: any[]; disks: DiskInfoCommunication[] }> = signal({ volumes: [], disks: [] });

  public fromGettingStarted: boolean;
  public compName = '';
  public compFullName = '';
  public compVmName = '';
  public email: string;
  public hid: string;
  public sourceHid: string;
  public sourceUserId: string;
  public initialFormData?: { [key: string]: any };
  public isNBF = false;
  public isNetBuild = false;
  public isOffline: boolean;
  public mode: PlanMode = PlanMode.create;
  public profile = '';
  public rdConfigurationId: string;
  public type: PlanTypes = PlanTypes.BackupDiskImagePlan;
  public userId: string;
  public targetComputerData: TargetComputerData;
  public wizardMode: PlansWizardMode = 0;
  public backupVersion: string;
  public wasNotExecuted: boolean;
  public firstStepTitle: string;
  public isLinux: boolean;
  public isMac: boolean;
  public isUnix: boolean;
  public Plan$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public dataBases$: BehaviorSubject<any> = new BehaviorSubject([]);
  public machinesDisksStatuses$: BehaviorSubject<MachinesDisksLoadedStatuses> = new BehaviorSubject(null);
  public password = new BehaviorSubject<string>('');
  public restoreIbbTypeModel?: RestoreIbbTypeModel;
  public restoreParams?: WizardRestoreSettings;

  private planIdValue: string;
  public set planId(value: string) {
    this.planIdValue = value || null; // Empty string is not equal null, and it is very important!
  }
  public get planId(): string {
    return this.planIdValue;
  }

  public abstract openErrorRunningPlan(): Observable<SaveVariants>;
}

@Injectable({ providedIn: 'root' })
export class RemoteManagementWizardsService extends RemoteManagementWizardsServiceAbstract {
  private auth = inject(AuthFacade);
  private loginFacade = inject(LoginFacade);
  private toastService = inject(ToastService);
  private http = inject(HttpClient);
  private modalService = inject(ModalService);
  private notificationFacade = inject(NotificationOptionsFacade);
  rdService = inject(RemoteDeployDataFacade);
  private i18nPipe = inject(I18NextPipe);
  private router = inject(Router);
  private environment = inject(EnvironmentService);
  private applicationStateFacade = inject(ApplicationStateFacade);
  private rmCommandsService = inject(RmCommandsService);

  /**
   * Emits value when a plans is saved in a configuration.
   * Emits tuple of <string, string>.
   * First value is configurationId, second - plans array
   */
  public plansSaved$: Subject<[string, RemoteDeployPlanModel[]]> = new Subject();
  public plansArray$: BehaviorSubject<any> = new BehaviorSubject([]);
  public AvailablePlansForRD$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public error = '';
  public hasAllRequiredDataToGetPlan = false;
  public accountId: string;
  public providerId: string;
  public backupVersionUpdated: string;
  public validVersionForSchedule: boolean;
  public canChangeRestoreVMType: boolean;
  public canShowRecoveryModel: boolean;
  public timeZoneOffset: number;
  public isProvider: boolean;
  public currentPrefix: string;
  public sessionId: string;
  public allowPrePostActions: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public planNotification: NotificationPerPlanSettings = new NotificationPerPlanSettings();

  private headerContentType = 'application/json; charset=utf-8';

  public get isRestore(): boolean {
    return RemoteManagementWizardsService.getWizardState(this).isRestore;
  }

  constructor() {
    super();
    const auth = this.auth;

    auth.currentUser$
      .pipe(first((user: Administrator) => user && user.ProviderInfo !== null && user.ProviderInfo !== undefined))
      .subscribe((user) => {
        this.timeZoneOffset = user.ProviderInfo.TimeZoneOffset;
        this.isProvider = user.IsProvider;
      });
  }

  private needShowErrorVersionModal(): boolean {
    return (
      this.isNBF &&
      (this.type === PlanTypes.BackupHyperV || this.type === PlanTypes.BackupVMware) &&
      this.backupVersionUpdated &&
      +this.backupVersionUpdated.substring(0, 3) < 761
    );
  }

  public setAvailablePlansForRD(id: string, plans: any): void {
    this.rdConfigurationId = id;
    const AvailablePlans = { [id]: {} };
    plans.forEach((p) => (AvailablePlans[id][p.plan.ID] = p));
    this.AvailablePlansForRD$.next(AvailablePlans);
  }

  public setWizardAndGetRequiredData(component: any, settings: ModalSettings, settingsForWizard: WizardSettings): afterCloseType {
    this.hasAllRequiredDataToGetPlan = false;
    return this.setDataAndOpenWizard(component, settings, settingsForWizard);
  }

  public startGenericRestoreWizard(computer?: Computer): void {
    this.openWizard(GenericRestoreWizardComponent, { size: MbsSize.lg, data: { computer } });
  }

  public setWizard(component: any, settings: ModalSettings, settingsForWizard: WizardSettings): afterCloseType {
    this.hasAllRequiredDataToGetPlan = true;
    return this.setDataAndOpenWizard(component, settings, settingsForWizard);
  }

  public showErrorVersionModalForUnix(): void {
    this.openErrorVersionModal(
      true,
      this.i18nPipe.transform('wizards:wizards_error_agent_version_text_alert'),
      this.i18nPipe.transform('wizards:wizards_error_agent_version_title', { format: 'title' })
    );
  }

  public needShowErrorVersionModalForUnix(isNbf = false): boolean {
    return (
      this.isUnix && (this.isRestore || this.isNBF || isNbf) && this.backupVersionUpdated && +this.backupVersionUpdated.substring(0, 2) < 41
    );
  }

  public updateVersionString(): void {
    if (!this.backupVersion) return;

    this.backupVersionUpdated = this.backupVersion ? this.backupVersion.split('.').join('') : '';

    if (this.backupVersionUpdated.length < 3) this.backupVersionUpdated += '0';

    const version = this.backupVersionUpdated ? +this.backupVersionUpdated.substring(0, 3) : 0;
    this.validVersionForSchedule = this.isUnix || version >= 741;

    if (this.isRestore) this.canChangeRestoreVMType = version >= AGENT_VERSION_FOR_CHANGE_TYPE;
    if (this.type === PlanTypes.DataBasePlan || this.vmTypes.includes(this.type)) {
      if (this.backupVersionUpdated.length === 3) this.backupVersionUpdated += '000';
      if (this.backupVersionUpdated.length === 4) this.backupVersionUpdated += '00';
      if (this.backupVersionUpdated.length === 5) this.backupVersionUpdated += '0';
    }
    if (this.type === PlanTypes.DataBasePlan && this.backupVersionUpdated) {
      const newVersion = +this.backupVersionUpdated.substring(0, 6);
      this.canShowRecoveryModel = newVersion >= VERSION_RECOVERY_MODEL;
    }
  }

  public initBaseDataInService(wizardSettings: WizardSettings): void {
    for (const key in wizardSettings) {
      if (!Object.prototype.hasOwnProperty.call(wizardSettings, key)) continue;

      this[key] = wizardSettings[key];
    }

    if (!wizardSettings.firstStepTitle) this.firstStepTitle = null;
  }

  private setDataAndOpenWizard(component: any, settings: ModalSettings, wizardSettings: WizardSettings): afterCloseType {
    this.initBaseDataInService(wizardSettings);

    if (this.backupVersion) {
      this.backupVersionUpdated = this.backupVersion ? this.backupVersion.split('.').join('') : '';

      if (this.backupVersionUpdated.length < 3) this.backupVersionUpdated += '0';

      if (this.needShowErrorVersionModalForUnix()) {
        this.showErrorVersionModalForUnix();

        return { afterClosed: of(null) };
      }

      const version = this.backupVersionUpdated ? +this.backupVersionUpdated.substring(0, 3) : 0;
      this.validVersionForSchedule = this.isUnix || version >= 741;

      if (this.isRestore) this.canChangeRestoreVMType = version >= AGENT_VERSION_FOR_CHANGE_TYPE;
      if (this.type === PlanTypes.DataBasePlan || this.vmTypes.includes(this.type)) {
        if (this.backupVersionUpdated.length === 3) this.backupVersionUpdated += '000';
        if (this.backupVersionUpdated.length === 4) this.backupVersionUpdated += '00';
        if (this.backupVersionUpdated.length === 5) this.backupVersionUpdated += '0';
      }
      if (this.type === PlanTypes.DataBasePlan && this.backupVersionUpdated) {
        const newVersion = +this.backupVersionUpdated.substring(0, 6);
        this.canShowRecoveryModel = newVersion >= VERSION_RECOVERY_MODEL;
      }
    }

    if (this.needShowErrorVersionModal()) {
      this.openErrorVersionModal();

      return { afterClosed: of(null) };
    }

    return this.openWizard(component, settings);
  }

  openErrorVersionModal(isInvalidFields = false, text = '', title = ''): void {
    this.modalService.openCustom(CantOpenModalComponent, { collapsing: true, data: { isInvalidFields, text, title } }).finally(noop);
  }

  openForceDeactivateFFIModal(): Promise<boolean> {
    return this.modalService.open(
      {
        header: { title: this.i18nPipe.transform('wizards:schedule.simpleMode.forceDeactivateFFI.title'), showCloseCross: false },
        footer: {
          okButton: { text: this.i18nPipe.transform('shared:buttons.yes') },
          cancelButton: { text: this.i18nPipe.transform('shared:buttons.no') }
        }
      },
      this.i18nPipe.transform('wizards:schedule.simpleMode.forceDeactivateFFI.description')
    );
  }

  openErrorRunningPlan(): Observable<SaveVariants> {
    return from(
      this.modalService.openCustom<SaveVariants>(CantOpenModalComponent, {
        collapsing: true,
        data: {
          isInvalidFields: true,
          showVariants: this.backupVersionUpdated && +this.backupVersionUpdated.substring(0, 3) >= 801,
          title: this.i18nPipe.transform('wizards:common.applyChanges'),
          text: this.i18nPipe.transform('wizards:common.planIsRunning'),
          alertType: MbsPopupType.warning,
          okButtonText: this.i18nPipe.transform('wizards:buttons.stopPlanAndSaveChanges'),
          cancelButtonText: this.i18nPipe.transform('shared:buttons.cancel')
        }
      })
    );
  }

  closeAndReset(): void {
    this.modalService.dismissAll();
    this.resetData();
  }

  resetData(): void {
    this.fromGettingStarted = false;
    this.isNBF = false;
    this.compName = '';
    this.compVmName = '';
    this.initialFormData = null;
    this.sessionId = null;
    this.targetComputerData = null;
    this.backupVersionUpdated = null;
    this.firstStepTitle = null;
    this.restoreParams = null;
    this.password.next('');
  }

  private openWizard(component: any, settings: ModalSettings): afterCloseType {
    const wizard = this.modalService.openRef(component, settings);
    if (wizard) {
      wizard.componentInstance.orientations = 'vertical';
      wizard.result.then(() => this.resetData()).catch(() => this.resetData());
      return { afterClosed: from(wizard.result) };
    }

    return null;
  }

  public GetRMPlanWithAdditionalRequests(): Observable<any> {
    const data = { computerHID: this.hid, profile: this.profile };
    const formData = new FormData();
    for (const key of Object.keys(data)) formData.append(key, data[key]);
    return this.http.post('/Admin/Computers/GetRMComputerInfo', formData).pipe(
      switchMap((info: any) => {
        if (info.isNotAuth) return this.showLoginInfo();
        this.compName = info.ComputerName || this.compName || '';
        this.email = info.Email;
        this.isOffline = info.IsOffline;
        this.profile = info.ProfileName;
        const linkArray = info.Link.split('&');
        linkArray.forEach((item) => {
          if (item.indexOf('hid') !== -1) {
            let hid = item.split('=')[1];
            hid = hid.replace('%7b', '{');
            hid = hid.replace('%7d', '}');
            this.hid = hid;
          }
        });
        if (this.mode === PlanMode.create) {
          return of(null);
        }
        return this.GetShortInfo();
      }),
      switchMap((shortInfo: any) => {
        if (this.mode === PlanMode.create) {
          return this.GetRMPlanModel();
        } else if (shortInfo && shortInfo.length) {
          const idx = shortInfo.findIndex(
            (item) =>
              (+PlanTypes[item.PlanType] === this.type ||
                (item.PlanType === 'ProxmoxBackup' && this.type === PlanTypes.BackupProxmox) ||
                (+PlanTypes[item.PlanType] === PlanTypes.RestoreHyperV && this.type === PlanTypes.RestoreHyperVToCustom) ||
                (+PlanTypes[item.PlanType] === PlanTypes.RestoreVMware && this.type === PlanTypes.RestoreVMwareToCustom)) &&
              (this.planId ? item.PlanId === this.planId : true)
          );

          if (idx !== -1) {
            if (+PlanTypes[shortInfo[idx].PlanType] === PlanTypes.RestoreHyperV && this.type === PlanTypes.RestoreHyperVToCustom) {
              this.type = PlanTypes.RestoreHyperV;
            }
            if (+PlanTypes[shortInfo[idx].PlanType] === PlanTypes.RestoreVMware && this.type === PlanTypes.RestoreVMwareToCustom) {
              this.type = PlanTypes.RestoreVMware;
            }

            this.planId = shortInfo[idx].PlanId;
            this.userId =
              (!shortInfo[idx].UserId || shortInfo[idx].UserId === GuidEmpty) && this.userId ? this.userId : shortInfo[idx].UserId;
            return this.GetRMPlanModel();
          } else {
            this.showErrorAndClose(shortInfo);
            return of(null);
          }
        } else return of(null);
      })
    );
  }

  public GetShortInfo(): Observable<any> {
    const data = {
      computerHID: this.hid,
      profile: this.profile || '',
      email: this.email,
      isOffline: this.isOffline ? 'True' : 'False'
    };
    const formData = new FormData();
    for (const key of Object.keys(data)) formData.append(key, data[key]);
    return this.http.post('/Admin/Computers/GetPlansShortInfo', formData);
  }

  public GetRMPlanModel(settings = null, notSet = false): Observable<any> {
    const params = settings || {
      mode: this.mode.toString(),
      type: this.type.toString(),
      email: this.email,
      planId: this.mode === PlanMode.edit ? this.planId : '',
      userId: this.userId,
      hid: this.hid,
      profile: this.profile || ''
    };

    return this.http
      .get('/Admin/PlansManagement/GetPlan', {
        params,
        headers: { 'Content-Type': 'application/json;charset=utf-8' }
      })
      .pipe(
        switchMap((plan: any) => this.processGetPlanResponse(plan, notSet)),
        catchError((err: HttpErrorResponse) => {
          if (!notSet) this.modalService.dismissAll();

          return throwError(() => err);
        })
      );
  }

  private processGetPlanResponse(plan: any, notSet = false): Observable<any> {
    if (this.wizardMode === PlansWizardMode.forRD) {
      if (!plan || !plan.plan) {
        this.showErrorAndClose(plan);
        return of(null);
      } else {
        if (!notSet) this.Plan$.next(plan);
        return of(plan);
      }
    } else {
      if (plan.isNotAuth) {
        this.showLoginInfo();
        return of(null);
      } else if (plan.StatusCode !== 200) {
        this.errorHandlerGetPlan(plan);
        return of(null);
      } else {
        if (!notSet) this.Plan$.next(plan.PayLoad);
        return of(plan.PayLoad);
      }
    }
  }

  errorHandlerGetPlan(plan): void {
    if (
      (this.type === PlanTypes.BackupHyperV || this.type === PlanTypes.BackupVMware) &&
      plan.PayLoad.includes('GuidAttributesAsUlong') &&
      plan.PayLoad.includes('VolumeInfoCommunication')
    ) {
      this.modalService.dismissAll();
      this.openErrorVersionModal(+this.backupVersionUpdated.substring(0, 3) >= 761);
    } else {
      this.showErrorAndClose(plan);
    }
  }

  showLoginInfo(): Observable<any> {
    const modalSettings: ModalSettings = {
      header: { title: 'Session Expired' },
      footer: { okButton: { text: 'Login' }, cancelButton: { show: false } }
    };
    this.modalService
      .open(modalSettings, 'Your session has expired. Please log in again.')
      .then(() => this.redirectToLogin())
      .catch(() => this.redirectToLogin());
    return of(null);
  }

  redirectToLogin(): void {
    this.loginFacade.logout();
  }

  public UpdateAndRunPlan(plan): Observable<any> {
    const payload = {
      agentType: 'backup',
      commandType: 'UpdatePlan',
      params: {
        planContent: JSON.stringify(plan),
        runAfterSave: true
      }
    };
    return this.rmCommandsService.remoteCommandByHid<unknown, any>(this.hid, payload).pipe(
      map((response) => {
        if (!response) throwError(() => new HttpErrorResponse({ error: response, status: 400 }));
        return response;
      })
    );
  }

  public checkSQLCredentials(params: SQLIsSysAdminOrCredentialsModel): Observable<{
    StatusCode?: number;
    PayLoad: boolean;
  }> {
    return this.http
      .post<{ PayLoad: boolean }>('Admin/PlansManagement/CheckSQLInstanceCredentials', params)
      .pipe(catchError(() => of({ PayLoad: false })));
  }

  public checkSQLInstanceIsSysadminRoleMember(params: SQLIsSysAdminOrCredentialsModel): Observable<{
    StatusCode?: number;
    PayLoad: boolean;
  }> {
    return this.http
      .post<{ PayLoad: boolean }>('Admin/PlansManagement/GetSQLInstanceIsSysadminRoleMember', params)
      .pipe(catchError(() => of({ PayLoad: false })));
  }

  public PreparePlanBeforePostRM(plan): any {
    if (this.mode === PlanMode.create) {
      const isConsistency = this.type === PlanTypes.ConsistencyCheck;
      plan.ID = isConsistency ? plan.ConnectionID : this.generateUUID();
      if (isConsistency) this.mode = PlanMode.edit;
      this.planId = plan.ID;
    }
    return plan;
  }

  public PostRMPlan(plan, settings = null): Observable<any> {
    const runAfterSave = !!(settings?.fromGettingStarted || this.fromGettingStarted);
    const dataFromTarget = this.targetComputerData && !this.sessionId;

    let params: HttpParams = new HttpParams()
      .append('mode', settings?.mode || this.mode.toString())
      .append('type', settings?.type || this.type.toString())
      .append('email', dataFromTarget ? this.targetComputerData?.email : settings?.email || this.email)
      .append('userId', dataFromTarget ? this.targetComputerData?.userId : settings?.userId || this.userId)
      .append('hid', dataFromTarget ? this.targetComputerData?.hid : settings?.hid || this.hid)
      .append('computerName', dataFromTarget ? this.targetComputerData?.computerName : settings?.computerName || this.compName)
      .append('profile', settings?.profile || this.profile)
      .append('runAfterSave', runAfterSave.toString());

    if (this.targetComputerData || this.sessionId) {
      params = params.append('externalUserId', this.sessionId && this.sourceUserId ? this.sourceUserId : this.userId);
    }

    return this.http
      .post<Record<string, unknown> | { Error: boolean; Message: string }>('Admin/PlansManagement/PostPlan', plan, {
        params: params,
        headers: { 'Content-Type': this.headerContentType }
      })
      .pipe(
        map((response) => {
          const hiddenError = response?.Error;

          if (!response) throwError(() => new HttpErrorResponse({ error: response, status: 400 }));
          if (hiddenError) {
            this.toastService.error(String(response.Message));

            throw new Error(String(response.Message));
          }

          return response;
        })
      );
  }

  public createRestoreVerificationPlan(params: CreateRVPlanParams): Observable<boolean> {
    return this.http.post<boolean>('/Admin/PlansManagement/CreateRestoreVerificationPlan', params, {
      headers: { 'Content-Type': 'application/json;charset=utf-8' }
    });
  }

  updateArrayOfPlans(dataModel: RemoteDeployPlanModel): any {
    if (!dataModel.PlanType) dataModel.PlanType = this.type;

    const arrayOfPlansToSave = this.plansArray$.value;

    if (this.mode === PlanMode.create) {
      dataModel.plan.ID = this.generateUUID();
      arrayOfPlansToSave.push(dataModel);
    } else {
      const idx = arrayOfPlansToSave.findIndex((p) => p.plan.ID === (dataModel as any).ID);
      arrayOfPlansToSave[idx].plan = dataModel;
      this.updatePlanTextElements(arrayOfPlansToSave[idx], this.rdService.allStorageAccounts);
      this.updatePlanTextRetention(arrayOfPlansToSave[idx]);
      this.setValidForRDStatuses(arrayOfPlansToSave[idx]);
    }

    this.plansArray$.next(arrayOfPlansToSave);
    this.setAvailablePlansForRD(this.rdConfigurationId, arrayOfPlansToSave);
    return dataModel.plan;
  }

  setValidForRDStatuses(wrapper: RemoteDeployPlanModel): void {
    const plan = wrapper.plan;
    wrapper.invalidForRd =
      !!plan.IsHybridBackup ||
      !!plan.HybridConnections?.length ||
      (plan.HybridID && plan.HybridID !== GuidEmpty) ||
      (plan.HybridRetentionPolicy && !plan.HybridRetentionPolicy.RetentionUseDefaultSettings) ||
      (wrapper.PlanType === PlanTypes.BackupDiskImagePlan &&
        (plan.BackupVolumes === BackupTargetVolumesEnum.SelectedOnly || plan.ExcludeEnabled || this.existSomeExcludeRule(plan.DiskInfo)));
  }

  existSomeExcludeRule(DiskInfo): boolean {
    return (
      !!DiskInfo &&
      !!DiskInfo.length &&
      !!DiskInfo[0].Volumes &&
      !!DiskInfo[0].Volumes.length &&
      DiskInfo[0].Volumes.some((v) => v.BackupOptions && v.BackupOptions.ExcludeRules.length)
    );
  }

  getUpdatePlansForRD(plans: RemoteDeployPlanModel[], storages: StorageAccountLegacy[]): RemoteDeployPlanModel[] {
    if (plans && plans.length) {
      return plans.map((plan) => {
        plan = WizardBaseForPlanHelper.ToPaskalCase(plan) as RemoteDeployPlanModel;
        this.updatePlanTextElements(plan, storages);
        this.updatePlanTextRetention(plan);
        this.setValidForRDStatuses(plan);
        return plan;
      });
    }

    return [];
  }

  updatePlanTextElements(plan: RemoteDeployPlanModel, storageAccounts: StorageAccountLegacy[]): void {
    const full = plan.plan.ForceFullSchedule;
    const schedule = plan.plan.Schedule;
    const isPlan = plan.PlanType === PlanTypes.Plan;
    plan.BucketDisplayName = updatePlanBucketDisplayName(plan, storageAccounts);

    if (this.isNBF || plan.plan.IsArchive) {
      const isFull = full && full.Enabled;
      const scheduleArchive = isFull ? plan.plan.ForceFullSchedule : plan.plan.Schedule;

      plan.BucketDisplayName = updatePlanBucketDisplayName(plan, storageAccounts);
      plan.DisplayFullSchedule = scheduleArchive.Enabled
        ? (scheduleArchive.RecurType !== RecurType.Instantly
            ? isFull
              ? 'Full Backup: '
              : plan?.plan?.ForwardIncremental
                ? 'Forever Forward Incremental: '
                : 'Incremental backup: '
            : '') + ScheduleDataHelper.GetScheduleSummaryFromScheduleSettings(scheduleArchive)
        : '';
      plan.DisplaySchedule =
        isFull && plan.plan.Schedule.Enabled && plan.plan.Schedule.RecurType !== RecurType.Instantly
          ? ' Incremental Backup: ' + ScheduleDataHelper.GetScheduleSummaryFromScheduleSettings(plan.plan.Schedule)
          : '';

      return;
    }

    plan.DisplayFullSchedule =
      full && full.Enabled
        ? (schedule.RecurType !== RecurType.Instantly ? (isPlan ? 'Incremental backup: ' : 'Full Backup: ') : '') +
          ScheduleDataHelper.GetScheduleSummaryFromScheduleSettings(full)
        : '';
    plan.DisplaySchedule =
      schedule && schedule.Enabled && schedule.RecurType !== RecurType.Instantly
        ? (isPlan ? ' Block-level Backup: ' : ' Block-level Incremental Backup: ') +
          ScheduleDataHelper.GetScheduleSummaryFromScheduleSettings(schedule)
        : '';
  }

  updatePlanTextRetention(planWrapper: RemoteDeployPlanModel): void {
    const text: string[] = [];
    const step = StepsDataFromPlanHelper.updateRetentionPolicy(planWrapper.plan, false, planWrapper.PlanType, this.isUnix);
    if (!step.RetentionUseDefaultSettings) {
      if (step.deleteVersionsOlderThan) {
        text.push(`Delete versions older than: ${step.deleteVersionsOlderThanCount} ${Period[step.deleteVersionsOlderThanPeriod]}`);
        if (planWrapper.PlanType === PlanTypes.Plan) {
          text.push(`from ${FromTime[step.fromTimeSelected]}`);
        }
      }
      if (step.alwaysKeepLastVersion) text.push('Always keep the last version');
      if (step.keepNumberOfVersions) text.push(`Keep number of versions: ${step.RetentionNumberOfVersions} for each file`);
      if (step.delayPurgeForEnabled) text.push(`Delay purge for: ${step.delayPurgeFor} ${Period[step.deleteVersionsOlderThanPeriod]}`);
      if (step.deleteIfLocallyDeleted) text.push(`Delete after: ${step.deleteAfter} days`);
      if (step.doNotShowWarning) text.push('Do not show warning for files to be deleted');
    }
    if (planWrapper.plan.IsArchive && planWrapper.plan.GFSPolicySettings && planWrapper.plan.GFSPolicySettings.IsEnabled) {
      const GFSSettings = planWrapper.plan.GFSPolicySettings;
      if (GFSSettings.Weekly && GFSSettings.Weekly.IsEnabled) {
        text.push(`Period of Keeping Weekly Full Backups: ${GFSSettings.Weekly.StorePeriod}`);
      }
      if (GFSSettings.Monthly && GFSSettings.Monthly.IsEnabled) {
        text.push(`Period of Keeping Monthly Full Backups: ${GFSSettings.Monthly.StorePeriod}`);
      }
      if (GFSSettings.Yearly && GFSSettings.Yearly.IsEnabled) {
        text.push(`Period of Keeping Yearly Full Backups: ${GFSSettings.Yearly.StorePeriod}`);
      }
      if (GFSSettings.ImmutabilityEnabled) {
        text.push('Immutability Enabled');
      }
    }
    planWrapper.RetentionText = text;
  }

  deletePlanFromArray(planId: string): Observable<boolean> {
    const arrayOfPlans: any[] = Array.from(this.plansArray$.value);
    const index = arrayOfPlans.findIndex((p) => p.plan.ID === planId);
    if (~index) {
      arrayOfPlans.splice(index, 1);
      this.plansArray$.next(arrayOfPlans);
    }
    this.setAvailablePlansForRD(this.rdConfigurationId, arrayOfPlans);
    return of(true);
  }

  public GetPlanNotificationOptions(): void {
    this.http
      .get<NotificationPerPlanSettings>(`api/plans/${this.planId}/${this.hid}/${this.userId}/notification-options`)
      .pipe(first((x) => !!x))
      .subscribe((result: NotificationPerPlanSettings) => {
        this.planNotification = result;
        this.getGlobalNotification();
      });
  }

  public getGlobalNotification(): void {
    this.notificationFacade.load();
  }

  public updateDestinationAsDefault(value: string, needSet: boolean): void {
    if (!needSet) return;

    const hid = this.targetComputerData && !this.sessionId ? this.targetComputerData?.hid : this.hid;

    this.applicationStateFacade
      .getUiStorage()
      .pipe(
        take(1),
        switchMap((storage) => {
          const storageValue = storage?.[UiStorageKey.destinationAsDefault];
          const destinationAsDefault = storageValue ? JSON.parse(storageValue) : {};

          if (value === destinationAsDefault[hid]) return of(null);

          destinationAsDefault[hid] = value;

          return this.applicationStateFacade.updateStorageSetting(UiStorageKey.destinationAsDefault, JSON.stringify(destinationAsDefault));
        }),
        catchError(() => of(null)),
        first()
      )
      .subscribe();
  }

  public updateOverdueAlert(overdueAlert: OverdueAfterSuccess, name: string, settings?): Observable<any> {
    const dataFromTarget = this.targetComputerData && !this.sessionId;
    const viewModel = {
      userEmail: dataFromTarget ? this.targetComputerData.email : this.email || settings?.email,
      userId: dataFromTarget ? this.targetComputerData.userId : this.userId || settings?.userId,
      planId: this.planId || settings?.planId,
      planName: name,
      hid: dataFromTarget ? this.targetComputerData.hid : this.hid || settings?.hid,
      computerName: dataFromTarget ? this.targetComputerData.computerName : this.compName || settings?.compName,
      overdueAlert: overdueAlert
    };
    return this.http.post('/Admin/Monitoring/UpdateOverdueAlert', viewModel);
  }

  public SavePlanNotification(notification: NotificationPerPlanSettings, hid?: string): Observable<any> {
    const innerHid = this.targetComputerData && !this.sessionId ? this.targetComputerData.hid : this.hid;
    return this.http.put('api/plans/notification-options', { ...notification, hid: hid || innerHid });
  }

  public showErrorAndClose(data: any): void {
    this.modalService.dismissAll();
    const message = 'Error. The plan was not loaded, please try again later.';
    if (data.PayLoad === 'USER_NO_DESTINATIONS') {
      this.toastService.error('The user has no backup destinations assigned. Please, consider adding backup destinations to the user.');
    } else if (data.code === 'MBS-ERROR-0001' || data.PayLoad === 'MBS-ERROR-0001') this.toastService.error(data.message || message);
    else this.toastService.error(message);
  }

  public generateUUID(): string {
    let date = new Date().getTime();
    let date2 = (performance && performance.now && performance.now() * 1000) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      let r = Math.random() * 16;
      if (date > 0) {
        r = (date + r) % 16 | 0;
        date = Math.floor(date / 16);
      } else {
        r = (date2 + r) % 16 | 0;
        date2 = Math.floor(date2 / 16);
      }
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  public navigateToCloud(): void {
    this.router.navigate([RoutingPath.ApRestoreToCloud]);
  }

  static getWizardState(mainService: RemoteManagementWizardsService): WizardState {
    const isRDMode = mainService.wizardMode === PlansWizardMode.forRD;
    const isRestore = RestoreTypes.includes(mainService.type);
    const isSQLPlan = mainService.type === PlanTypes.DataBasePlan;
    const isHyperV = mainService.type === PlanTypes.BackupHyperV;
    const isVMWare = mainService.type === PlanTypes.BackupVMware;
    const rightVersion = +mainService.backupVersionUpdated?.substring(0, 2) >= AGENT_VERSION_FOR_APP_AWARE;
    const isAppAwareEnabled = rightVersion && (isHyperV || isVMWare);
    const isClusterSupported = isRDMode || (rightVersion && isHyperV);

    return {
      isSQLPlan,
      isRDMode,
      isRestore,
      isHyperV,
      isVMWare,
      isAppAwareEnabled,
      isClusterSupported
    };
  }
}
